import { SnapshotModel } from '../index';
import { Collections } from '../../constants';
import { CustomerOrganization, ESnapshotExists } from '../../types';
import { getErrorReporter } from '../../utils/errors';
import { AffidavitReconciliationSettings } from '../../types/organization';
import { removeUndefinedFields } from '../../helpers';
import { ColumnService } from '../../services/directory';
import { getOverrideAAC } from '../../types/affidavits/convertARS';
import { OrganizationModel } from './organizationModel';
import { safeGetModelFromRef } from '../getModel';
import { ResponseOrError, wrapError, wrapSuccess } from '../../types/responses';
import { isOrderRate, OrderRate } from '../../types/rates';
import {
  BadRequestError,
  ColumnError,
  NotFoundError,
  wrapErrorAsColumnError
} from '../../errors/ColumnErrors';
import { safeGetOrThrow } from '../../safeWrappers';

export class CustomerOrganizationModel extends SnapshotModel<
  CustomerOrganization,
  typeof Collections.customerOrganizations
> {
  type = Collections.customerOrganizations;

  // TODO: replace with model once one exists?
  private publisher: OrganizationModel | null = null;

  public async getPublisher(): Promise<ResponseOrError<OrganizationModel>> {
    if (this.publisher) {
      return wrapSuccess(this.publisher);
    }

    return safeGetModelFromRef(
      OrganizationModel,
      this.ctx,
      this.modelData.organization
    );
  }

  /**
   * Records the id this customer organization has in a publisher's ad system.
   * Integrations persist advertiser ids through here rather than through refs.
   */
  public async setInternalID(internalID: string): Promise<void> {
    await this.update({ internalID });
  }

  public async updateAutomatedAffidavitConfiguration(
    updates: Partial<AffidavitReconciliationSettings>
  ): Promise<void> {
    const { affidavitReconciliationSettings } = this.modelData;

    const { response: publisher, error: publisherError } =
      await this.getPublisher();

    if (publisherError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.AFFIDAVITS,
        publisherError,
        'Failed to get publisher for customer organization',
        { customerOrganizationId: this.id }
      );
      return;
    }

    const newAffidavitReconciliationSettings: Partial<AffidavitReconciliationSettings> =
      {
        ...affidavitReconciliationSettings,
        ...updates
      };

    const { response: automatedAffidavitConfiguration, error: aacError } =
      await getOverrideAAC(
        this.ctx,
        publisher,
        newAffidavitReconciliationSettings
      );

    if (aacError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.AFFIDAVITS,
        aacError,
        'Failed to get automated affidavit configuration from affidavit reconciliation settings for publisher',
        { publisherId: this.id }
      );
      return await this.ref.update({
        affidavitReconciliationSettings: newAffidavitReconciliationSettings
      });
    }

    await this.ref.update(
      removeUndefinedFields({
        affidavitReconciliationSettings: newAffidavitReconciliationSettings,
        automatedAffidavitConfiguration
      })
    );
  }

  public async getDefaultFilingTypeRate(
    filingTypeId: string,
    { isDisplayLayout } = { isDisplayLayout: false }
  ): Promise<ResponseOrError<ESnapshotExists<OrderRate> | null, ColumnError>> {
    const customerFilingTypeRates = this.modelData.filingTypeRates || {};
    const customerFilingTypeRateConfig =
      customerFilingTypeRates[filingTypeId] || {};

    const rateRef = isDisplayLayout
      ? customerFilingTypeRateConfig.displayRate
      : customerFilingTypeRateConfig.linerRate;

    if (!rateRef) {
      return wrapSuccess(null);
    }

    const { response: rateSnap, error: rateError } = await safeGetOrThrow(
      rateRef
    );
    if (rateError) {
      return wrapErrorAsColumnError(rateError, NotFoundError);
    }

    const rate = rateSnap.data();

    if (!isOrderRate(rate)) {
      return wrapError(
        new BadRequestError('Received rate inapplicable to order')
      );
    }

    return wrapSuccess(rateSnap as ESnapshotExists<OrderRate>);
  }
}
